<script lang="ts" setup>
import { useDevice } from '#imports'
import { GAME_CATEGORIES, STATUS_LABEL } from '~/resources'
import type { GameCategoryItem } from '~/types/index'
import { PREFIX_ID_GAME_FISHING, PREFIX_ID_GAME_SLOTS } from '~/constants'

const { onPlayGame } = usePlayGame()
const { isMobile, isDesktop } = useDevice()
const { jackpots, totalJackpots } = useJackpot()

useHead({
  link: GAME_CATEGORIES.map((x) => ({
    rel: 'preload',
    as: 'fetch',
    href: x.bannerPC!,
    type: 'application/json',
    crossorigin: 'anonymous',
    fetchpriority: 'high',
  })),
})

const getJackpotByGameId = (patternGameId: string): number => {
  if (!patternGameId) {
    return 0
  }
  return Object.entries(jackpots.value)
    .filter((item) => item[0].includes(patternGameId))
    .reduce((total: number, jackpotItem: [string, number]) => {
      return total + jackpotItem[1]
    }, 0)
}

const totalJackpotFishing = computed(() => {
  return getJackpotByGameId(PREFIX_ID_GAME_FISHING) ?? 0
})

const totalJackpotSlots = computed(() => {
  return totalJackpots.value - totalJackpotFishing.value
})

const getJackpotByProvider = (prefixId: string): number => {
  if (prefixId === PREFIX_ID_GAME_FISHING) {
    return totalJackpotFishing.value ?? 0
  }
  if (prefixId === PREFIX_ID_GAME_SLOTS) {
    return totalJackpotSlots.value ?? 0
  }
  return 0
}

const gameMappingJackpotCategories = computed(() => {
  return GAME_CATEGORIES.map((item) => ({
    ...item,
    jackpotNumber: getJackpotByProvider(item.patternGameId ?? ''),
  }))
})

const getGameLink = (item: GameCategoryItem) => {
  if (item.link) {
    return item.link
  }
  return ''
}
</script>
<template>
  <div
    :class="[
      { 'categories-desktop': isDesktop },
      { 'categories-mobile': isMobile },
    ]"
  >
    <div class="game-categories">
      <a
        v-for="menu in gameMappingJackpotCategories"
        :key="menu.title"
        :href="getGameLink(menu)"
        class="game-categories__item"
        @click.prevent="
          onPlayGame({
            link: menu.link,
            loginRequired: menu.loginRequired,
            newTab: menu.isNewTab,
          })
        "
      >
        <div class="game-categories__item-content">
            <div
              v-if="menu.jackpotNumber"
              class="game-categories__jackpot-number"
            >
              <BaseAnimatedNumber :number="menu.jackpotNumber" :start="menu.jackpotNumber" />
            </div>
          <div class="game-categories__thumbnail">
            <img
              class="game-categories__thumbnail-img"
              :src="isMobile ? menu?.bannerMB : menu.bannerPC"
              :alt="`${menu.title} thumbnail`"
              width="188"
              height="133"
            />
          </div>
          <div
            class="game-categories__footer"
            :class="{ 'bottom-footer': isMobile && menu.type !== 'sports' }"
          >
            <template v-for="(status, key) in STATUS_LABEL" :key="key">
              <div
                v-if="menu.label?.includes(status.value)"
                class="game-categories__label"
                :class="`game-categories__label--${status.label}`"
              >
                <div class="game-categories__label-text">
                  <span
                    class="game-categories__label-icon"
                    :class="status.icon"
                  />
                  {{ status.label }}
                </div>
              </div>
            </template>
            <div class="game-categories__wrapper-name">
              <div class="game-categories__item-name line-limit-1">
                {{ menu.title }}
              </div>
              <span
                v-if="!isMobile || (isMobile && menu.type === 'sports')"
                class="game-categories__footer-icon icon-right"
              />
            </div>
          </div>
        </div>
      </a>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~/assets/styles/components/desktop/home/game-category.scss';
@import '~/assets/styles/components/responsive/desktop/home/game-category.scss';
</style>
<style lang="scss">
@import '~/assets/styles/components/responsive/desktop/home/game-category-desktop-mini.scss';
</style>
